import { useState, useEffect, useCallback, useRef } from "react";
import LoaderIcon from "./LoaderIcon";
import useOutsideClick from "../../hooks/useOutsideClick";

const Caution = ({ title }) => (
  <p className="text-[12px] text-gray-500 text-center py-1.5">{title}</p>
);

const PaginatedDropdown = ({
  datakey = "",
  searchkey = "search",
  url,
  placeHolder = "Search options...",
  selectedValue = null, // { value, label }
  onChange,
  getAll = false,
  mapOptions,
  error = false,
  disabled = false,
  setTouched = () => { },
}) => {
  const [options, setOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [noMoreOptions, setNoMoreOptions] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [horizontalOffset, setHorizontalOffset] = useState(0);

  const [containerRef] = useOutsideClick({
    state: isOpen,
    callBack: () => setIsOpen(false),
  });
  const searchInputRef = useRef(null);

  // Debounce
  const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch Options
  const fetchOptions = async (search = "", pageNum = 1) => {
    if (noMoreOptions && pageNum !== 1) return;

    if (pageNum === 1 && searchQuery !== search) {
      setOptions([]);
    }

    setIsLoading(true);
    try {
      const fetchUrl = `${url}${url.includes("?") ? "" : "?"}sortOrder=ASC&size=${getAll ? 1000 : 10}&page=${getAll ? 0 : pageNum - 1
        }${search ? `&${searchkey}=${search}` : ""}`;

      const resp = await fetch(fetchUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-JWT-Assertion": localStorage.getItem("token"),
        },
      });

      if (!resp.ok) throw new Error(resp.statusText);
      const response = await resp.json();

      const isWrapped = response?.result && response?.data;
      const responseCode = isWrapped ? response.result.responseCode : 200;
      const rawData = isWrapped ? response.data?.[datakey] ?? response.data : response;

      if (responseCode === 200) {
        const newOptions = mapOptions(Array.isArray(rawData) ? rawData : []);
        setIsEmpty(pageNum === 1 && newOptions.length === 0);

        setOptions((prev) => {
          const merged = pageNum === 1 ? newOptions : [...prev, ...newOptions];
          return merged.filter(
            (opt, idx, self) => idx === self.findIndex((o) => o.value === opt.value)
          );
        });

        if (!getAll && isWrapped && response.data.totalPages === response.data.pageNo + 1) {
          setNoMoreOptions(true);
        } else if (newOptions.length === 0 && pageNum > 1) {
          setNoMoreOptions(true);
        }
        return true;
      }
    } catch (err) {
      console.error("Error fetching dropdown options:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetch = useCallback(debounce(fetchOptions, 300), [url]);

  useEffect(() => {
    if (isOpen && options.length === 0 && searchQuery === "" && page === 1) {
      fetchOptions("", 1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && (searchQuery !== "" || page !== 1)) {
      debouncedFetch(searchQuery, page);
    }
  }, [isOpen, searchQuery, page, debouncedFetch]);

  // Focus search input and scroll into view when opened
  useEffect(() => {
    if (isOpen) {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }
  }, [isOpen]);

  // Handlers
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
    setNoMoreOptions(false);
  };

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  // Detect horizontal position before opening dropdown
  useEffect(() => {
    if (isOpen) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const dropdownWidth = rect.width;
        const rightEdge = rect.left + dropdownWidth;
        const viewportWidth = window.innerWidth;

        if (rightEdge > viewportWidth) {
          setHorizontalOffset(viewportWidth - rightEdge - 16);
        } else if (rect.left < 0) {
          setHorizontalOffset(-rect.left + 16);
        } else {
          setHorizontalOffset(0);
        }
      }
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const loadMore = (e) => {
    e.stopPropagation();
    if (!getAll && !noMoreOptions) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Trigger Area */}
      <div
        onClick={toggleDropdown}
        className={`w-full border rounded-md px-3 py-2 min-h-[38px] cursor-pointer flex items-center justify-between
            dark:bg-gray-800 dark:text-gray-100 bg-white
            ${error ? "border-red-500 ring-1 ring-red-300" : "border-gray-300 focus-within:ring-1 focus-within:ring-brand-300"}
            ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
      >
        <div className="flex items-center w-full mr-2 overflow-hidden">
          {selectedValue ? (
            <div className="flex items-center justify-between w-full">
              <span className="text-sm truncate dark:text-gray-100">{selectedValue.label}</span>
              <button
                type="button"
                onClick={handleClear}
                className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                &times;
              </button>
            </div>
          ) : (
            <span className="text-gray-400 text-sm truncate">{placeHolder}</span>
          )}
        </div>

        <div className="flex-shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{ left: horizontalOffset }}
          className="absolute z-20 w-full max-h-80 overflow-hidden flex flex-col bg-white border border-gray-300 rounded-md shadow-lg 
            dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 top-full mt-1"
        >
          {/* Sticky Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-0 z-10">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder={placeHolder}
              className="w-full border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto flex-1">
            {options.map((option) => {
              const isSelected = selectedValue?.value === option.value;
              return (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 flex justify-between items-center ${isSelected ? "bg-brand-50 text-brand-900 font-medium" : "text-gray-800 dark:text-gray-100"
                    }`}
                >
                  <span>{option.label}</span>
                  {isSelected && <span className="text-brand-600 font-bold">✓</span>}
                </div>
              );
            })}

            {isLoading && (
              <Caution
                title={
                  <>
                    Loading <LoaderIcon className="inline-block w-3 h-3 ml-1 animate-spin" />
                  </>
                }
              />
            )}

            {!isLoading && isEmpty && options.length === 0 && <Caution title="No data available" />}

            {!isLoading && !noMoreOptions && options.length > 0 && (
              <div
                onClick={loadMore}
                className="px-3 py-2 text-center text-xs text-gray-500 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                Load more
              </div>
            )}

            {!isLoading && noMoreOptions && options.length > 0 && <Caution title="No more options" />}
          </div>
        </div>
      )}
    </div>
  );
};

export const mapOptions = (records, valueKey = "id", labelFormatter) => {
  const data = Array.isArray(records) ? records : records?.records ?? [];
  return data.map((record) => ({
    value: record[valueKey],
    label: labelFormatter(record),
    obj: record,
  }));
};

export default PaginatedDropdown;
